import { useEffect, useState, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cm } from '~/utils/utils'

export type DownloadProgressEstimator = (params: {
  elapsedMs: number
  currentProgress: number
}) => number

interface DownloadProgressButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  isLoading?: boolean
  progressPct?: number
  estimateProgress?: DownloadProgressEstimator
  completionVisibleMs?: number
}

const DEFAULT_COMPLETION_VISIBLE_MS = 300

const defaultEstimateProgress: DownloadProgressEstimator = ({ elapsedMs, currentProgress }) => {
  const easedProgress = Math.round(95 * (1 - Math.exp(-elapsedMs / 3500)))
  return Math.max(currentProgress, easedProgress)
}

export function DownloadProgressButton({
  children,
  className,
  isLoading = false,
  progressPct,
  estimateProgress = defaultEstimateProgress,
  completionVisibleMs = DEFAULT_COMPLETION_VISIBLE_MS,
  ...props
}: DownloadProgressButtonProps) {
  const isDeterminate = typeof progressPct === 'number'
  const boundedProgress = Math.max(0, Math.min(100, Math.round(progressPct ?? 100)))
  const [estimatedProgress, setEstimatedProgress] = useState(0)
  const [shouldRenderLoading, setShouldRenderLoading] = useState(isLoading)
  const [isCompleting, setIsCompleting] = useState(false)
  const displayProgress = isCompleting ? 100 : isDeterminate ? boundedProgress : estimatedProgress

  useEffect(() => {
    if (isLoading) {
      setShouldRenderLoading(true)
      setIsCompleting(false)
      return
    }

    if (!shouldRenderLoading) return

    setIsCompleting(true)
    const timeoutId = window.setTimeout(() => {
      setShouldRenderLoading(false)
      setIsCompleting(false)
      setEstimatedProgress(0)
    }, completionVisibleMs)

    return () => window.clearTimeout(timeoutId)
  }, [completionVisibleMs, isLoading, shouldRenderLoading])

  useEffect(() => {
    if (!isLoading || isDeterminate) {
      setEstimatedProgress(0)
      return
    }

    setEstimatedProgress(0)
    const startedAt = Date.now()
    const intervalId = window.setInterval(() => {
      const elapsedMs = Date.now() - startedAt
      setEstimatedProgress(prev => {
        const nextProgress = estimateProgress({ elapsedMs, currentProgress: prev })
        return Math.max(prev, Math.min(95, Math.round(nextProgress)))
      })
    }, 120)

    return () => window.clearInterval(intervalId)
  }, [estimateProgress, isLoading, isDeterminate])

  return (
    <button
      aria-busy={shouldRenderLoading || undefined}
      className={cm(
        'relative overflow-hidden transition-all duration-300',
        className,
        shouldRenderLoading &&
          'h-4 min-w-[112px] rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0 text-indigo-900 hover:bg-indigo-50 active:bg-indigo-50'
      )}
      {...props}
    >
      {shouldRenderLoading ? (
        <span className='flex w-full items-center gap-2'>
          <span className='relative h-1.5 min-w-16 flex-1 overflow-hidden rounded-full bg-indigo-100'>
            <span
              className={cm(
                'absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-indigo-400/70 via-violet-400/70 to-fuchsia-400/70 shadow-[inset_-8px_0_12px_-8px_rgba(255,255,255,0.6)] transition-[width] duration-300 ease-out',
                !isDeterminate && 'animate-pulse'
              )}
              style={{ width: `${displayProgress}%` }}
            />
          </span>
          <span className='whitespace-nowrap text-[11px] font-medium leading-none tabular-nums'>
            {displayProgress}%
          </span>
        </span>
      ) : (
        children
      )}
    </button>
  )
}
